//@ts-nocheck
import EmbedTab from './EmbedTab';
import DeleteModal from '../DeleteModal/DeleteModal';
import GeneratePdfOptions from './TripDownloadPdf';
import ShareTab from './ShareTab';
import NewTripHeaderActions from './NewTripHeaderActions';
import { Box, Modal, Tooltip } from '@mui/material';
import {
  ShareNetwork,
  Trash,
  ListMagnifyingGlass
} from '@phosphor-icons/react';
import { useState, useEffect } from 'react';
import { XGButton, XGTabs } from 'xg-cl';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import { ErrorToast } from '../../../utils/alerts';
import { deleteTrip } from '../../../services/trip/trip';
import { useFlags } from 'launchdarkly-react-client-sdk';
import * as PropTypes from 'prop-types';
import './TripHeaderActions.css';

const ConditionalTooltip = ({ children, title, show }) => {
  if (!show) return children;

  return (
    <Tooltip title={title} arrow placement='top'>
      <span style={{ display: 'inline-block' }}>{children}</span>
    </Tooltip>
  );
};
ConditionalTooltip.propTypes = {
  children: PropTypes.node.isRequired,
  title: PropTypes.string.isRequired,
  show: PropTypes.bool.isRequired
};

const TripHeaderActions = ({ id }) => {
  const trip = useSelector((state) => state.trip);
  const navigate = useNavigate();
  const [openShareDialog, setOpenShareDialog] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
  const [isSmallScreen, setIsSmallScreen] = useState(false);
  const { t } = useTranslation(['trip', 'common', 'embed']);
  const { tripid, ispublic } = trip;
  const [isPublic, setIsPublic] = useState(ispublic);
  const { tripNavigation, enableTripNewActions } = useFlags();
  const SMALL_SCREEN_BREAKPOINT = 1056.98;

  useEffect(() => {
    const handleResize = () => {
      setIsSmallScreen(window.innerWidth < SMALL_SCREEN_BREAKPOINT);
    };

    handleResize();
    window.addEventListener('resize', handleResize);

    return () => window.removeEventListener('resize', handleResize);
  }, []);

  const toggleShareDialog = () => {
    setOpenShareDialog(!openShareDialog);
    localStorage.removeItem('customColor');
    localStorage.removeItem('customText');
  };

  const modalTabs = [
    {
      title: 'Share',
      content: (
        <ShareTab
          tripid={tripid}
          isPublic={isPublic}
          setIsPublic={setIsPublic}
        />
      )
    },
    { title: 'Embed', content: <EmbedTab tripid={tripid} /> }
  ];

  const openTab = () => {
    window.open(`/itineraries/${tripid}`, '_blank');
  };

  const handleDeleteTripClick = async () => {
    setIsDeleteModalOpen(true);
  };

  const handleOnConfirmRemoval = async () => {
    try {
      setIsDeleting(true);
      await deleteTrip(tripid);
      navigate('/trips');
    } catch (error) {
      ErrorToast(t('errors:tripCouldNotBeDeleted', { reason: error.message }));
    } finally {
      setIsDeleting(false);
    }
  };

  const handleOnCancelRemoval = () => {
    setIsDeleteModalOpen(false);
  };

  return (
    <>
      {enableTripNewActions ? (
        <NewTripHeaderActions
          id={id}
          isPublic={isPublic}
          setIsPublic={setIsPublic}
        />
      ) : (
        <div
          className='trip-header-actions'
          id={id}
          data-testid='trip-header-actions-component'
        >
          <GeneratePdfOptions tripId={tripid} />

          <ConditionalTooltip title={t('common:delete')} show={isSmallScreen}>
            <XGButton
              startIcon={!isSmallScreen && <Trash width={23} />}
              icon={isSmallScreen && <Trash width={17} />}
              data-testid='itinerary-delete-button'
              id='itinerary-delete-button'
              text={isSmallScreen ? '' : t('common:delete')}
              type='secondary'
              size='large'
              variant='outlined'
              onClick={handleDeleteTripClick}
              style={{
                padding: isSmallScreen ? '8px' : '8px 14px',
                minWidth: isSmallScreen ? '40px' : 'auto'
              }}
            />
          </ConditionalTooltip>

          <ConditionalTooltip title={t('common:share')} show={isSmallScreen}>
            <XGButton
              startIcon={!isSmallScreen && <ShareNetwork width={17} />}
              icon={isSmallScreen && <ShareNetwork width={17} />}
              data-testid='trip-header-actions-share-button'
              id='itinerary-share-button'
              text={isSmallScreen ? '' : t('common:share')}
              type='secondary'
              size='large'
              variant='outlined'
              onClick={toggleShareDialog}
              style={{
                padding: isSmallScreen ? '8px' : undefined,
                minWidth: isSmallScreen ? '40px' : 'auto'
              }}
            />
          </ConditionalTooltip>

          <ConditionalTooltip title={t('common:preview')} show={isSmallScreen}>
            <XGButton
              startIcon={!isSmallScreen && <ListMagnifyingGlass width={23} />}
              icon={isSmallScreen && <ListMagnifyingGlass width={23} />}
              data-testid='itinerary-preview-button'
              id='itinerary-preview-button'
              text={isSmallScreen ? '' : t('common:preview')}
              type={tripNavigation ? 'secondary' : 'primary'}
              size='large'
              variant={tripNavigation ? 'outlined' : 'contained'}
              onClick={openTab}
              style={{
                padding: isSmallScreen ? '8px' : undefined,
                minWidth: isSmallScreen ? '40px' : 'auto'
              }}
            />
          </ConditionalTooltip>

          <Modal
            open={openShareDialog}
            onClose={toggleShareDialog}
            data-testid='trip-header-actions-modal'
          >
            <Box className='trip-modal-box'>
              <XGTabs id='modal-tabs' tabs={modalTabs} />
              <div className='share-dialog-close-button'>
                <XGButton
                  data-testid='share-dialog-close-button'
                  id='dialog-close-button'
                  text={t('common:close')}
                  type='secondary'
                  size='large'
                  variant='outlined'
                  onClick={toggleShareDialog}
                ></XGButton>
              </div>
            </Box>
          </Modal>

          <DeleteModal
            id='delete-amenity-modal'
            isOpenModal={isDeleteModalOpen}
            title={t('trip:deleteTrip')}
            onConfirm={handleOnConfirmRemoval}
            onCancel={handleOnCancelRemoval}
            description={`${t('trip:deleteTripConfirmation')} ${t(
              'common:thisActionCannotBeUndone'
            )}`}
            isSubmitting={isDeleting}
          />
        </div>
      )}
    </>
  );
};

TripHeaderActions.propTypes = {
  id: PropTypes.string
};

export default TripHeaderActions;
